import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
  CircularProgress
} from "@mui/material";

// Icons
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Firebase
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import db, { storage } from "../../firebase";


const AddEditVehicle = ({ onBack, formId }) => { // Receives formId
  const [form, setForm] = useState({
    vehicleId: "", 
    makeModel: "",
    fuelType: "Diesel",
    isEV: false,
    currentlyOperatedBy: "",
    inspectedBy: "",
    policyNumber: "",
    insuranceCompany: "",
    isZeroDebt: false,
    agentName: "",
    agentContact: "",
    hasClaims: "No",
    claimDetails: "",
    nextPucDate: "",
    nextInsuranceDate: "",
    pucDocUrl: "",
    insuranceDocUrl: "",
    serviceBillDocUrl: "",
    claimDocUrl: ""
  });

  const [uploading, setUploading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // --- 1. FETCH DATA IF EDITING ---
  useEffect(() => {
    // If formId exists, we are in EDIT mode
    if (formId) {
      const fetchData = async () => {
        setFetching(true);
        try {
          const docRef = doc(db, "VehicleData", formId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setForm({ ...docSnap.data() }); // Populate form
          } else {
            alert("Vehicle not found!");
          }
        } catch (error) {
          console.error("Error fetching vehicle:", error);
        } finally {
          setFetching(false);
        }
      };
      fetchData();
    }
  }, [formId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileRef = ref(storage, `vehicle_docs/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      setForm((prev) => ({
        ...prev,
        [fieldName]: url,
      }));

      alert(`${fieldName.replace("DocUrl", "")} uploaded successfully!`);
    } catch (error) {
      console.error("Upload failed", error);
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleViewDoc = (url) => {
    if (url) window.open(url, "_blank");
    else alert("No document uploaded yet.");
  };

  // --- 2. HANDLE SAVE (ADD OR UPDATE) ---
  const handleSave = async () => {
    try {
        if (formId) {
            // UPDATE EXISTING
            const docRef = doc(db, "VehicleData", formId);
            await updateDoc(docRef, { ...form, updatedAt: serverTimestamp() });
            alert("Vehicle Updated Successfully");
        } else {
            // CREATE NEW
            await addDoc(collection(db, "VehicleData"), {
                ...form,
                createdAt: serverTimestamp(),
            });
            alert("Vehicle Created Successfully");
        }
        
        if(onBack) onBack(); // Go back to list
    } catch (error) {
        console.error(error);
        alert("Error saving vehicle");
    }
  };

  // Helper Row Component
  const DocumentRow = ({ label, docFieldName, fileUrl }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
      <Typography variant="body2" fontWeight="500" color="#333">
        {label}
      </Typography>
      <Box display="flex" gap={1}>
        <input
          accept="image/*,application/pdf"
          style={{ display: "none" }}
          id={`raised-button-file-${docFieldName}`}
          type="file"
          onChange={(e) => handleFileUpload(e, docFieldName)}
        />
        <label htmlFor={`raised-button-file-${docFieldName}`}>
          <Button
            variant="contained"
            component="span"
            color="error"
            size="small"
            disabled={uploading}
            sx={{ mr: 1, textTransform: "none", minWidth: "80px" }}
            startIcon={uploading ? <CircularProgress size={20} color="inherit"/> : <CloudUploadIcon />}
          >
            {fileUrl ? "Re-Upload" : "Upload"}
          </Button>
        </label>
        <Button
          variant="outlined"
          size="small"
          color="inherit"
          onClick={() => handleViewDoc(fileUrl)}
          disabled={!fileUrl}
          sx={{ 
            textTransform: "none", 
            borderColor: fileUrl ? "#d32f2f" : "#ccc", 
            color: fileUrl ? "#d32f2f" : "#ccc",
            minWidth: "70px" 
          }}
          startIcon={<VisibilityIcon />}
        >
          View
        </Button>
      </Box>
    </Box>
  );

  if (fetching) {
      return (
          <Box display="flex" justifyContent="center" p={5}>
              <CircularProgress />
          </Box>
      );
  }

  return (
    <SitePage>
        <Grid container spacing={2} sx={{ marginTop: "5px" }}>
        {/* === LEFT COLUMN === */}
        <Grid item xs={12} lg={6}>
            <SectionHeader>Vehicle & Compliance</SectionHeader>

            <TextField
            fullWidth
            size="small"
            label="Vehicle ID"
            name="vehicleId"
            value={form.vehicleId}
            onChange={handleChange}
            placeholder="Enter Vehicle ID"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            />

            
  <Box sx={{ mb: 2 }}>
  <Typography
    variant="caption"
    sx={{ fontWeight: "bold", color: "text.secondary" }}
  >
    Fuel Type
  </Typography>

  <RadioGroup
    row
    name="fuelType"
    value={form.fuelType}
    onChange={handleChange}
    sx={{ alignItems: "center" }}
  >
    <FormControlLabel value="Diesel" control={<Radio size="small" />} label="Diesel" />
    <FormControlLabel value="CNG" control={<Radio size="small" />} label="CNG" />
    <FormControlLabel value="Petrol" control={<Radio size="small" />} label="Petrol" />
    <FormControlLabel value="Hybrid" control={<Radio size="small" />} label="Hybrid" />

    {/* EV Checkbox INLINE */}
    <FormControlLabel
      control={
        <Checkbox
          checked={form.isEV || false}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, isEV: e.target.checked }))
          }
          size="small"
        />
      }
      label="Is EV?"
      sx={{
        ml: 2,
        "& .MuiFormControlLabel-label": {
          fontWeight: 600,
          fontSize: "0.9rem",
        },
      }}
    />
  </RadioGroup>
</Box>
            


            <SubHeader>Compliance Date Overview</SubHeader>
            <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
                <TextField fullWidth size="small" value="Next PUC Date" disabled sx={{ bgcolor: "#f8f9fa" }} />
            </Grid>
            <Grid item xs={6}>
                <TextField 
                    fullWidth 
                    size="small" 
                    type="date"
                    name="nextPucDate"
                    value={form.nextPucDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={6}>
                <TextField fullWidth size="small" value="Next Insurance Date" disabled sx={{ bgcolor: "#f8f9fa" }} />
            </Grid>
            <Grid item xs={6}>
                <TextField 
                    fullWidth 
                    size="small" 
                    type="date"
                    name="nextInsuranceDate"
                    value={form.nextInsuranceDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>

            <SubHeader>Required Document Links</SubHeader>
            <DocumentRow label="PUC Document" docFieldName="pucDocUrl" fileUrl={form.pucDocUrl} />
            <DocumentRow label="Insurance Policy" docFieldName="insuranceDocUrl" fileUrl={form.insuranceDocUrl} />
            <DocumentRow label="Last Service Bill" docFieldName="serviceBillDocUrl" fileUrl={form.serviceBillDocUrl} />
        </Grid>

        {/* === RIGHT COLUMN === */}
        <Grid item xs={12} lg={6}>
            <SectionHeader>Insurance Details</SectionHeader>

            <TextField
            fullWidth
            size="small"
            label="Policy Number"
            name="policyNumber"
            value={form.policyNumber}
            onChange={handleChange}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            />
            
            <TextField
            fullWidth
            size="small"
            label="Insurance Company"
            name="insuranceCompany"
            value={form.insuranceCompany}
            onChange={handleChange}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            />

            <FormControlLabel
            sx={{ mb: 2 }}
            control={
                <Checkbox
                checked={form.isZeroDebt}
                onChange={handleChange}
                name="isZeroDebt"
                />
            }
            label="Is Zero Debt?"
            />

           
        </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={5} pt={2} borderTop="1px solid #eee">
        <Button 
            variant="contained" 
            color="inherit" 
            sx={{ bgcolor: "#e0e0e0", color: "#333", boxShadow: 'none' }}
            onClick={onBack} 
        >
            Cancel
        </Button>
        <Button 
            variant="contained" 
            color="error" 
            onClick={handleSave}
            endIcon={<CheckIcon />}
        >
            {formId ? "Update Vehicle" : "Save Vehicle"}
        </Button>
        </Box>

    </SitePage>
  );
};

// --- STYLED COMPONENTS ---

const SitePage = styled.div`
  padding: 30px;
  background-color: #fff;
  overflow: hidden;
  box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2),
    0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12);
  border-radius: 4px;
`;

const TitleContainer = styled.div`
  margin-bottom: 20px;
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
    color: #333;
  }
  p {
    font-size: 0.9rem;
    color: #666;
    margin: 5px 0 0 0;
  }
`;

const AlertBox = styled.div`
  background-color: #ffc107;
  color: #000;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-size: 0.9rem;
`;

const SectionHeader = styled.h6`
  font-weight: bold;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
  margin-bottom: 20px;
  font-size: 1.1rem;
  color: #212529;
`;

const SubHeader = styled.h6`
  font-weight: bold;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
  margin-bottom: 15px;
  margin-top: 25px;
  font-size: 0.95rem;
  color: #495057;
`;

export default AddEditVehicle;